
import Vue from 'vue'
import axios from 'axios'
import { Prop, Component, Watch } from 'vue-property-decorator'
import FavouritesManager, { ListSetting, ListFilter } from './favourites'
import { generateParams, getOperatorTitle } from '../../functions/query'
import { downloadFile } from '../../functions/util'
import customiseDialog from './customise.vue'
import bulkSelection from './bulkSelection.vue'
import listExport from './export.vue'
import ListTable, { ListColumn, ListOptions } from './Table.vue'
import { ListWarn } from './warn.vue'
import { ListFilterOption } from './filters.vue'
import { getHeaderKey } from './list.utils'

type ListAction = any

type BulkListAction = any

@Component({
  components: {
    bulkSelection,
    customiseDialog,
    listExport,
    ListTable
  }
})
export default class ListComponent<T extends Record<string, any>> extends Vue {
  @Prop({ type: String, default: () => null })
  private readonly type!: string

  @Prop({ type: String, required: true })
  private readonly apiRoute!: string

  @Prop({ type: String, required: true })
  private readonly favouritesKey!: string

  @Prop({ type: String, default: null })
  private readonly searchField!: string

  @Prop({ type: String, default: 'Search' })
  private readonly searchFieldLabel!: string

  @Prop({ type: Array, required: true })
  private readonly filterOptions!: ListFilterOption[]

  @Prop({ type: Array, required: true })
  private readonly availableColumns!: ListColumn<T>[]

  @Prop({ type: Array, required: true })
  private readonly defaultColumns!: string[]

  @Prop({ type: Array, default: () => [] })
  private readonly actions!: ListAction[]

  @Prop({ type: Array, default: () => [] })
  private readonly multiActions!: BulkListAction[]

  @Prop({ type: Array, default: () => [] })
  private readonly queryParams!: string[][]

  @Prop({ type: Object, default: () => ({}) })
  private readonly dataExcel!: any

  @Prop({ type: String, default: null })
  private readonly exportName!: string

  @Prop({ type: Boolean, default: false })
  private readonly hideCustomize!: boolean

  @Prop({ type: [Array, Object], default: null })
  private readonly warn?: ListWarn<T> | ListWarn<T>[]
  
  @Prop({ type: String, default: null })
  private readonly defaultSortField!: string

  @Prop({ type: Object, default: null })
  private readonly authDataSearch!: any

  selected: T[] = []

  configuring = false

  isReady = false

  options: ListOptions = {
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    sortDesc: []
  }

  rowsPerPageItems: number[] = [10, 25, 50, 75, 100]

  activeFilters: ListFilter[] = []

  visibleColumns: string[] = []

  searchText = ''

  get headers() {
    const validHeaders: ListColumn<T>[] = []
    this.visibleColumns.forEach((key) => {
      const foundColumn = this.availableColumns.find(h => getHeaderKey(h) === key)
      if (foundColumn) {
        validHeaders.push(foundColumn)
      }
    })
    return validHeaders
  }

  get columns() {
    return this.visibleColumns.filter(c => !!this.availableColumns.find(a => getHeaderKey(a) === c))
  }

  get mobileView() {
    return ['xs', 'sm'].includes(this.$vuetify.breakpoint.name)
  }

  @Watch('options', { deep: true })
  private optionsChanged(value: ListOptions) {
    this.setSetting()
    this.updateResults(value)
  }

  mounted() {
    this.configureFavourites()
  }

  private table() {
    return this.$refs.table as ListTable<T>
  }

  resetList() {
    this.activeFilters = []
    this.visibleColumns = this.defaultColumns
    this.options.sortBy = []
    this.options.sortDesc= []

    if (this.defaultSortField) {
        this.options.sortBy = [this.defaultSortField]
    }

    this.updateResults()
    FavouritesManager.unsetSetting(this.favouritesKey)
  }

  addFilter(filter: ListFilter) {
    this.activeFilters.push(filter)
    this.updateResults()
    this.setSetting()
  }

  removeFilter(filter: ListFilter) {
    this.activeFilters = this.activeFilters.filter(f => f.value !== filter.value)
    this.activeFilters = [...this.activeFilters]
    this.updateResults()
    this.setSetting()
  }

  newVisible(columns: string[]) {
    this.visibleColumns = columns
    this.setSetting()
  }

  configureSettings(setting: ListSetting) {
    this.configuring = true
    try {
      this.visibleColumns = setting.columns

      this.activeFilters = setting.filters.map((filter) => {
        const fieldName = this.filterOptions.find(f => f.key === filter.field)?.text
        const operatorName = getOperatorTitle(filter.operator)
        filter.text = `'${fieldName}' ${operatorName} '${filter.displayValue || filter.value}'`
        return filter
      })
      if (!setting.sortByField && this.defaultSortField) {
          this.options.sortBy = [this.defaultSortField]
        } else {
          this.options.sortBy = [setting.sortByField]
        }

      this.options.sortDesc = [setting.sortByOrder === 'DESC']
      this.options.itemsPerPage = setting.itemsPerPage || 10
    } finally {
      this.configuring = false
    }
  }

  setSetting() {
    FavouritesManager.setSetting(this.favouritesKey, {
      sortByField: this.options.sortBy[0],
      sortByOrder: this.options.sortDesc[0] ? 'DESC' : 'ASC',
      columns: this.visibleColumns,
      filters: this.activeFilters,
      itemsPerPage: this.options.itemsPerPage
    })
  }

  configureFavourites() {
    const existingSetting = FavouritesManager.checkSetting(this.favouritesKey)
    try {
      if (existingSetting) {
        this.configureSettings(existingSetting)
      } else {
        this.visibleColumns = this.defaultColumns
      }
    } catch {
      this.visibleColumns = this.defaultColumns
      this.activeFilters = []
    } finally {
      this.isReady = true
    }
  }

  updateResults(options?: ListOptions) {
    if (this.isReady && this.table()) {
      this.table().updateResults(options)
    }
  }

  downloadServerExport(type: 'page' | 'all') {
    const options = { ...this.options }
    if (type === 'all') {
      delete options.itemsPerPage
      delete options.page
    }

    const path = this.apiRoute.startsWith('/') ? `${axios.defaults.baseURL}${this.apiRoute}` : `${axios.defaults.baseURL}/${this.apiRoute}`

    downloadFile(`${path}/export?${generateParams(options, this.activeFilters, this.queryParams)}`)
  }

  toggleFilter(filter: ListFilter) {
    filter.disabled = !filter.disabled
    this.updateResults()
  }

  applySearch() {
    if (this.searchText && this.searchText !== '') {
      const fieldName = this.filterOptions.find(f => f.key === this.searchField)?.text
      const newFilter: ListFilter = {
        field: this.searchField,
        operator: 'lk',
        value: this.searchText,
        text: `${fieldName} Contains '${this.searchText}'`,
        disabled: false
      }
      this.addFilter(newFilter)
      this.searchText = ''
    }
  }
}
